import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { getUser } from "../lib/auth";
import { toast } from "sonner";
import { MessageSquare, Send, Trash2, Loader2, ShieldCheck } from "lucide-react";

export const Comments = ({ articleId }) => {
  const user = getUser();
  const [comments, setComments] = useState([]);
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/articles/${articleId}/comments`).then((r) => setComments(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (articleId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setBusy(true);
    try {
      await api.post(`/articles/${articleId}/comments`, { content });
      toast.success("Komentar terkirim");
      setContent("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengirim komentar");
    }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus komentar ini?")) return;
    try {
      await api.delete(`/comments/${id}`);
      toast.success("Komentar dihapus");
      load();
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  return (
    <section className="mt-12 pt-8 border-t border-white/10" data-testid="comments-section">
      <h2 className="font-heading text-2xl font-bold flex items-center gap-2 mb-2">
        <MessageSquare className="w-5 h-5 text-cyan1" /> Komentar ({comments.length})
      </h2>
      <p className="text-xs text-slate-500 font-mono flex items-center gap-1 mb-6">
        <ShieldCheck className="w-3 h-3 text-green-400" /> Dimoderasi AI — komentar kasar / spam / di luar topik ditolak otomatis
      </p>

      {user ? (
        <form onSubmit={submit} className="card p-4 mb-8" data-testid="comment-form">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={`Tulis komentar sebagai ${user.name || user.email}...`}
            rows={3}
            maxLength={1000}
            className="w-full bg-void border border-white/10 rounded-lg p-3 outline-none focus:border-cyan1/50 text-sm"
            data-testid="comment-input"
          />
          <div className="flex justify-between items-center mt-3">
            <span className="text-xs text-slate-600 font-mono">{content.length}/1000</span>
            <button disabled={busy || !content.trim()} className="btn-primary !py-2 !px-4 text-sm" data-testid="comment-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Kirim
            </button>
          </div>
        </form>
      ) : (
        <div className="card p-5 mb-8 text-center" data-testid="comment-login-cta">
          <p className="text-slate-400 text-sm">
            <Link to="/login" className="text-cyan1 hover:underline">Masuk atau daftar</Link> untuk ikut berdiskusi.
          </p>
        </div>
      )}

      <div className="space-y-4">
        {comments.length === 0 && <p className="text-slate-600 text-sm" data-testid="comments-empty">Belum ada komentar. Jadilah yang pertama!</p>}
        {comments.map((c) => (
          <div key={c.id} className="card p-4 flex gap-3" data-testid={`comment-${c.id}`}>
            {c.user_picture ? (
              <img src={c.user_picture} alt="" className="w-9 h-9 rounded-full shrink-0" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan1/40 to-magenta1/40 flex items-center justify-center font-heading font-bold text-sm shrink-0">
                {(c.user_name || "?")[0].toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-heading font-bold text-sm">{c.user_name}</span>
                <span className="text-[10px] font-mono text-slate-600">
                  {new Date(c.created_at).toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
              <p className="text-sm text-slate-300 mt-1 whitespace-pre-wrap break-words">{c.content}</p>
            </div>
            {user && (user.id === c.user_id || user.role === "admin") && (
              <button onClick={() => remove(c.id)} className="text-slate-600 hover:text-red-400 shrink-0" data-testid={`delete-comment-${c.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};
